import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import 'core-js/stable'
import 'regenerator-runtime/runtime'
import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'

import { Provider } from 'react-redux'
import { ThemeProvider, StylesProvider } from '@material-ui/styles'
import { StyledEngineProvider } from '@mui/material/styles'
import './bootstrap'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { CookiesProvider, Cookies } from 'react-cookie'
import TagManager from 'react-gtm-module'
import {
  ThemeProvider as ReferaThemeProvider,
  ConfirmContextProvider,
  LocalizationProvider,
  AdapterMoment,
} from '@refera/ui-web'
import ReferaTheme from '@refera/ui-core'

import { GTM_ID, APP_VERSION } from '_config/environment'
import { User } from '_models'
import { COOKIES_OPTIONS } from '_utils/constants'
import { theme } from '_utils/material-ui'

import configureStore from './store/configure-store'
import Router from './router'
import { ToastProvider } from './hooks/use-toast'
import { DialogProvider } from './hooks/use-dialog'
import { attachRefreshTokenInterceptor } from './services/axios-interceptors/refreshToken'
import axiosInstance, { formDataInstance, blobInstance } from '_services/requests'
import { logout, setAuthToken } from './modules/authentication/actions'

const cookies = new Cookies()
const rootElement = document.getElementById('root')

const authToken = cookies.get(COOKIES_OPTIONS.authTokenName, { path: COOKIES_OPTIONS.path })
const initialState = {
  user: new User({
    authToken,
  }),
}
const store = configureStore(initialState)

attachRefreshTokenInterceptor({
  axiosInstanceList: [axiosInstance, formDataInstance, blobInstance],
  onRefreshError: () => store.dispatch(logout()),
  storeTokenInLocalStateFn: token => store.dispatch(setAuthToken(token)),
})

if (GTM_ID) {
  TagManager.initialize({
    gtmId: GTM_ID,
  })
}

function checkVersion() {
  const currentVersion = cookies.get('appVersion')
  if (currentVersion !== APP_VERSION) {
    // Limpa todos os cookies da aplicação
    Object.keys(cookies.getAll()).forEach(cookieName => {
      cookies.remove(cookieName, { path: '/' })
      cookies.remove(cookieName, { path: COOKIES_OPTIONS.path })
    })

    // Limpa localStorage e sessionStorage
    if (typeof Storage !== 'undefined') {
      localStorage.clear()
      sessionStorage.clear()
    }

    // Limpa cache do service worker se disponível
    if ('serviceWorker' in navigator && 'caches' in window) {
      caches
        .keys()
        .then(cacheNames => {
          return Promise.all(
            cacheNames.map(cacheName => {
              return caches.delete(cacheName)
            })
          )
        })
        .then(() => {
          // Define a nova versão e recarrega
          cookies.set('appVersion', APP_VERSION, { path: COOKIES_OPTIONS.path })
          window.location.reload(true) // Force reload from server
        })
    } else {
      // Define a nova versão e recarrega se service worker não estiver disponível
      cookies.set('appVersion', APP_VERSION, { path: COOKIES_OPTIONS.path })
      window.location.reload(true) // Force reload from server
    }
  }
}

function inIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

if (!inIframe) checkVersion()

const root = createRoot(rootElement)

function App() {
  useEffect(() => {
    // Remove o loader inline da aplicação
    const initialLoader = document.getElementById('initial-loader')
    if (initialLoader) {
      initialLoader.remove()
    }
  }, [])

  return (
    <Provider store={store}>
      <StylesProvider injectFirst>
        <StyledEngineProvider injectFirst>
          <ReferaThemeProvider theme={ReferaTheme}>
            <ThemeProvider theme={theme}>
              <ConfirmContextProvider>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <CookiesProvider>
                      <DialogProvider>
                        <ToastProvider>
                          <Router />
                        </ToastProvider>
                      </DialogProvider>
                    </CookiesProvider>
                  </MuiPickersUtilsProvider>
                </LocalizationProvider>
              </ConfirmContextProvider>
            </ThemeProvider>
          </ReferaThemeProvider>
        </StyledEngineProvider>
      </StylesProvider>
    </Provider>
  )
}

root.render(<App />)
